import type { Block, BlockLevel, MeResponse } from "../../api/types";
import { hasPermission } from "../../auth/permissions";

export function isExecution(me: MeResponse): boolean {
  return me.membership.role.toLowerCase() === "execution";
}

// creatableLevels mirrors the server's create rules: blocks:create_top for a
// Project, blocks:create_sub for a Block or Sub-block. Execution holds
// create_sub but may only request a child under a non-top block it owns, so a
// Block (whose parent is a Project) is out of its reach.
export function creatableLevels(me: MeResponse): BlockLevel[] {
  const levels: BlockLevel[] = [];
  if (hasPermission(me, "blocks:create_top")) {
    levels.push("top");
  }
  if (hasPermission(me, "blocks:create_sub")) {
    if (!isExecution(me)) {
      levels.push("mid");
    }
    levels.push("ic");
  }
  return levels;
}

// parentCandidates are the loaded blocks a new block of `level` can hang off —
// a Block's parent is a Project, a Sub-block's is a Block — alphabetically by
// picker label. Execution only gets Blocks it owns (exec owner or assignee),
// the same ownership check the server applies.
export function parentCandidates(blocks: Block[], level: BlockLevel, me: MeResponse): Block[] {
  if (level === "top") {
    return [];
  }
  const parentLevel: BlockLevel = level === "mid" ? "top" : "mid";
  const execution = isExecution(me);
  const blocksById = new Map(blocks.map((block) => [block.id, block]));
  return blocks
    .filter((block) => block.level === parentLevel)
    .filter((block) => !execution || block.exec_owner?.id === me.user.id || block.assignee?.id === me.user.id)
    .sort((a, b) => parentOptionLabel(a, blocksById).localeCompare(parentOptionLabel(b, blocksById)));
}

// parentOptionLabel names a parent in the picker. Block names repeat across
// Projects, so a Block is shown under its Project, e.g. "Connect › Blueprints".
export function parentOptionLabel(parent: Block, blocksById: Map<string, Block>): string {
  const project = parent.parent_id === null ? undefined : blocksById.get(parent.parent_id);
  return project === undefined ? parent.name : `${project.name} › ${parent.name}`;
}

// canAddChild applies the same rules to a single block's "add child" form: the
// child's level must be creatable and this block a valid parent for it.
export function canAddChild(block: Block, me: MeResponse): boolean {
  const childLevel: BlockLevel | null = block.level === "top" ? "mid" : block.level === "mid" ? "ic" : null;
  return (
    childLevel !== null && creatableLevels(me).includes(childLevel) && parentCandidates([block], childLevel, me).length > 0
  );
}
